import React, { useState, useEffect, useRef, useMemo } from "react";
import styled, { keyframes, css } from "styled-components";
import theme from "../../../assets/theme";
import COLORS from "../../../assets/Colors";
import {
  Modal,
  RadioGroup,
  Radio,
  FormControlLabel,
  makeStyles,
  CircularProgress,
  Menu,
  MenuItem,
} from "@material-ui/core";
import { WhatsApp } from "@material-ui/icons";
import { Formik } from "formik";
import FormField from "../../common/FormField/FormField";
import useSettings from "../../../hooks/useSettings";
import SearchBar from "../../common/SearchBar";
import {
  CheckboxContainer,
  CheckBoxLabel,
  CustomCheckbox,
  CustomCheckBox,
  CustomFileUpload,
} from "../../common/FormInputs";
import useDebounce from "../../../hooks/useDebounce";
import SystemService from "../../../services/SystemService";
import _ from "lodash";
import queryString from "query-string";
import {
  EditorContainer,
  ButtonContainer,
  Label,
} from "../../pages/CaseDetails/common/CommentBox";
import AWSService from "../../../services/AWSService";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import ReactQuill from "react-quill";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCTAButton,
} from "../../common/Buttons";
import OrderDictation from "../../internal/OrderDictation/LiveDictation";
import CaseService from "../../../services/CaseService";
import { getErrorMessage } from "../../../helpers/functions";
import AddZoomLinkModal from "../../common/AddZoomLinkModal";
import useUser from "../../../hooks/useUser";
import { bulkUpdateValidationSchema } from "../../../helpers/validationSchema";
import * as XLSX from "xlsx";
import ResponseModal from "../../pages/AssignNeutralBulkCase/ResponseModal";

const ESCAPE_HTML_REGEX = /<[^>]+>/g;

const inputModes = { MANUAL: "manual", EXCEL: "excel" };

const resolutionKindOptions = [
  { label: "Arbitration", value: "arbitration" },
  { label: "Mediation", value: "mediation" },
  { label: "Negotiation", value: "negotiation" },
];

export const useStyles = makeStyles(() => ({
  button: {
    backgroundColor: COLORS.PRIMARY_WHITE,
    boxShadow: "none",
    width: "77px",
    height: "30px",
    color: COLORS.BTN_GREEN,
    textTransform: "Capitalize",
    border: "solid 1px #00838c",
    fontFamily: theme.fonts.primaryFontBold,
    fontSize: 14,
  },
  formLabel: {
    fontFamily: theme.fonts.primaryFontSemiBold,
    color: COLORS.COLOR_DARK,
  },
  table: {
    backgroundColor: "transparent",
    border: "1px solid #e1e3ee",
    borderRadius: 7,
    "& .MuiTableCell-root": { borderBottom: 0 },
  },
  menuRoot: {
    fontFamily: theme.fonts.primaryFontSemiBold,
    fontSize: 12,
    color: COLORS.COLOR_DARK,
  },
  tableHead: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: COLORS.INPUT_LABEL,
    fontSize: 12,
  },
  tableBody: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: COLORS.COLOR_DARK,
    fontSize: 12,
  },
}));

const BulkUpdate = ({ bulkUpdate, setBulkUpdate }) => {
  const classes = useStyles();
  const [inputText, setInputText] = useState("");
  const { caseLifeCycleStatus } = useSettings();
  const debounceValue = useDebounce(inputText, 500);
  const [neutralList, setNeutralList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState("");
  const quillRef = useRef();
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState(null);
  const [modal, setModal] = useState({ state: false });
  const { userObject } = useUser();
  const [mode, setMode] = useState(inputModes.MANUAL);
  const [excelCaseIds, setExcelCaseIds] = useState([]);
  const [contestedExcelData, setContestedExcelData] = useState([]);
  const [contestedExcelType, setContestedExcelType] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState({ state: false });

  useEffect(() => {
    if (bulkUpdate) getNeutralList(debounceValue);
  }, [bulkUpdate, debounceValue]);

  const inputs = [
    {
      label: "From CaseId",
      name: "fromCaseId",
      type: "number",
      required: true,
    },
    { label: "To CaseId", name: "toCaseId", type: "number", required: true },
    {
      type: "text",
      name: "expectCaseIds",
      label: "Except Case IDs (comma separated)",
      required: false,
      placeholder: "e.g., 102, 109, 120",
      helperText: "Optional: exclude specific cases from the selected range",
    },
    {
      type: "select",
      name: "resolutionKind",
      label: "Case Type",
      required: true,
      menuItems: resolutionKindOptions,
    },
  ];

  const radio_items = [
    {
      label: "Record the procedings",
      name: "recordingProceeding",
      value: "recordingProceeding",
    },
    {
      label: "Address label creation",
      name: "addressLabel",
      value: "addressLabel",
      disabled: false,
    },
    {
      label: "Case Status",
      name: "caseStatus",
      value: "caseStatus",
      disabled: true,
    },
    {
      label: "Assign Neutral",
      name: "assignNeutral",
      value: "assignNeutral",
      disabled: true,
    },
    {
      label: "Contested Case",
      name: "contestedCase",
      value: "contestedCase",
    },
  ];

  const server_items = [
    { label: "Transcription Server (1)", name: 1, value: 1 },
    { label: "Transcription Server (2)", name: 2, value: 2 },
  ];

  const handleFileUpload = (e, setFieldValue, isContested = false) => {
    const file = e.target.files[0];
    if (!file) return;
    setFieldValue("fileName", file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
        if (data.length === 0) {
          enqueueSnackbar("Excel file is empty", { variant: "error" });
          return;
        }
        const headers = (data[0] || []).map((h) =>
          (h || "").toString().toLowerCase().trim(),
        );

        let extracted = [];
        let type = null;

        const caseIdx = headers.findIndex((h) =>
          [
            "caseid",
            "case id",
            "id",
            "case_id",
            "case-id",
            "case id/dispute no",
          ].includes(h),
        );
        const contractIdx = headers.findIndex((h) =>
          [
            "contract number",
            "contract no",
            "contractno",
            "loan no",
            "loanno",
            "contract",
          ].includes(h),
        );

        if (caseIdx !== -1) {
          extracted = data
            .slice(1)
            .map((row) => row[caseIdx])
            .filter((id) => id !== undefined && id !== null && id !== "")
            .map(Number)
            .filter((id) => !isNaN(id) && id > 0);
          type = "caseId";
        } else if (contractIdx !== -1) {
          extracted = data
            .slice(1)
            .map((row) => row[contractIdx])
            .filter((val) => val !== undefined && val !== null && val !== "");
          type = "contractNo";
        }

        if (extracted.length === 0) {
          enqueueSnackbar("No valid data found in the file", {
            variant: "warning",
          });
          return;
        }

        if (isContested) {
          setContestedExcelData(extracted);
          setContestedExcelType(type);
        } else {
          setExcelCaseIds(extracted);
        }

        enqueueSnackbar(
          `${extracted.length} ${type === "caseId" ? "case ID" : "Contract Number"}${extracted.length > 1 ? "s" : ""} loaded successfully`,
          { variant: "success" },
        );
      } catch (err) {
        enqueueSnackbar("Failed to read Excel file", { variant: "error" });
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = null;
  };

  async function s3Upload(
    file,
    field,
    setFieldValue,
    fileTypes,
    accept,
    values,
    fileName,
  ) {
    if (file) {
      try {
        const types = [
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "application/vnd.ms-excel",
          "text/csv",
        ];
        setLoader({ state: true, message: `Uploading file ${file.name}` });
        if (!types.includes(file?.type))
          enqueueSnackbar("Invalid File Type", {
            variant: "error",
          });
        if (accept === ".xlsx") {
          const response = await readExcelFile(
            file,
            fileName,
            setFieldValue,
            values?.contractName,
          );
          setFieldValue(field, response);
          if (response?.length) setFieldValue(fileName, file.name);
        }
      } catch (err) {
        enqueueSnackbar(err?.message, { variant: "error" });
      } finally {
        setLoader({ state: false });
      }
    }
  }

  const readExcelFile = async (file, fileName, setFieldValue, contractName) => {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: "array" });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });
    const headerRow = data[0];
    const possibleHeaders = [
      "CONTRACT NUMBER",
      "Contract No",
      "Contract Number",
      "contract no",
      "Loan No",
      "loan no",
      "Loan no",
      contractName,
    ];
    const contractNumberIndex = headerRow.findIndex((h) =>
      possibleHeaders.includes(h.trim()),
    );
    if (contractNumberIndex === -1) {
      enqueueSnackbar("Contract number column not found", { variant: "error" });
    }
    return data
      .slice(1)
      .map((row) => row[contractNumberIndex])
      .filter(Boolean);
  };

  async function getNeutralList(query) {
    try {
      setLoading(true);
      let params = { page: 1, perPage: 1000, role: "mediator" };
      if (query) params.query = query;
      let stringParams = !_.isEmpty(params)
        ? `?${queryString.stringify(params)}`
        : "";
      const response = await SystemService.getProfiles(stringParams);
      if (response) setNeutralList(response?.data);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  }

  const imageHandler = (e) => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*, application/pdf");
    input.click();
    input.onchange = async () => {
      const file = input.files[0];
      const response = await uploadFileToServer(file);
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection();
      quill.insertText(range.index, file?.name, "link", response);
      quill.formatText(range.index, file?.name.length, { color: "#00838c" });
    };
  };

  const uploadFileToServer = async (file) => {
    try {
      setLoader({ state: true, message: `uploading ${file.name}` });
      const s3Response = await AWSService.getS3URL({ key: file.name });
      if (s3Response.url)
        return await AWSService.uploadToS3(s3Response.url, file);
    } catch (error) {
      enqueueSnackbar("File upload failed", { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [
            { align: "" },
            { align: "center" },
            { align: "right" },
            "bold",
            "italic",
            "underline",
            "blockquote",
            "image",
          ],
        ],
        handlers: { image: imageHandler },
      },
    }), // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const sendTypeOfData = (values) => {
    if (values?.typeOfLoad === "recordingProceeding") {
      let postbody = {
        content: values?.isFromZoom ? "" : comment,
        visibilityType: values?.visibilityType,
        whatsappRecipients: values?.whatsappRecipients,
      };
      if (values?.isFromZoom) postbody = { ...postbody, ...values?.isFromZoom };
      return postbody;
    } else if (values?.typeOfLoad === "caseStatus") {
      return { status: values.caseStatus, currentUser: userObject?.name };
    } else {
      return {
        individualMail: values?.individualMail,
        requestedMediators: values?.requestedMediators,
      };
    }
  };

  const handleModalClose = () => {
    setBulkUpdate(false);
    setMode(inputModes.MANUAL);
    setExcelCaseIds([]);
    setContestedExcelData([]);
    setContestedExcelType(null);
  };

  async function onFormSubmit(values, { resetForm }) {
    try {
      setLoader({ state: true, message: "Submitting response..." });
      const isAddressLabel = values?.typeOfLoad === "addressLabel";
      const isWithExcel = values?.labelType === "withExcel";
      const isWithCaseIds = values?.labelType === "withCaseIds";
      const isRecordProceedings = values?.typeOfLoad === "recordingProceeding";

      let payload;

      if (values.typeOfLoad === "contestedCase") {
        const contestedFlag = values.contestedFlag ?? true;
        if (mode === inputModes.MANUAL) {
          payload = {
            fromCaseId: values?.fromCaseId || null,
            toCaseId: values?.toCaseId || null,
            exceptCaseIds: values?.expectCaseIds
              ? values.expectCaseIds
                  .split(",")
                  .map((id) => Number(id.trim()))
                  .filter(Boolean)
              : [],
            contestedFlag,
          };
        } else {
          if (contestedExcelData.length === 0) {
            enqueueSnackbar("Please upload an Excel file before submitting", {
              variant: "warning",
            });
            setLoader({ state: false });
            return;
          }
          payload = {
            listOfIds: contestedExcelData,
            contestedFlag,
          };
        }
      } else if (isAddressLabel && isWithExcel) {
        payload = {
          contractNo: values?.contractNo,
          updates: { type: values?.typeOfLoad },
        };
      } else if (isRecordProceedings && mode === inputModes.EXCEL) {
        if (excelCaseIds.length === 0) {
          enqueueSnackbar(
            "Please upload an Excel file with Case IDs before submitting",
            { variant: "warning" },
          );
          setLoader({ state: false });
          return;
        }
        payload = {
          caseIds: excelCaseIds.map((id) => Number(id)),
          resolutionKind: values.resolutionKind,
          updates: { type: values?.typeOfLoad, data: sendTypeOfData(values) },
        };
      } else {
        if (
          mode === inputModes.MANUAL &&
          (!values.fromCaseId || !values.toCaseId)
        ) {
          enqueueSnackbar("Please enter both From Case ID and To Case ID", {
            variant: "warning",
          });
          setLoader({ state: false });
          return;
        }
        payload = {
          fromCaseId: values?.fromCaseId || null,
          toCaseId: values?.toCaseId || null,
          expectCaseIds: values?.expectCaseIds,
          resolutionKind: values.resolutionKind,
          updates: {
            type: values?.typeOfLoad,
            ...(isAddressLabel && isWithCaseIds
              ? {}
              : { data: sendTypeOfData(values) }),
          },
        };
      }

      let response;
      if (values?.typeOfLoad === "contestedCase") {
        response = await CaseService.contestedBulkUpdates(payload);
      } else {
        response = await CaseService.bulkUpdates(payload);
      }
      if (values?.typeOfLoad === "addressLabel" && response?.data)
        window.open(response?.data);
      if (response) {
        setLoader({ state: false });
        setModal({ state: false });
        setTimeout(
          () => setShowResponseModal({ state: true, metaData: response }),
          100,
        );
        enqueueSnackbar(response?.message, { variant: "success" });
        resetForm();
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
      setLoader({ state: false });
    } finally {
      setAnchorEl(null);
      setModal({ state: false });
      setComment("");
    }
  }

  return (
    <>
      <Modal open={bulkUpdate} onClose={handleModalClose} disableRestoreFocus>
        <Overlay>
          <ModalCard>
            <ModalHeader>
              <HeaderAccent />
              <HeaderContent>
                <IconBadge>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </IconBadge>
                <ModalTitle>Bulk Update</ModalTitle>
              </HeaderContent>
              <CloseBtn onClick={handleModalClose} aria-label="Close">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </CloseBtn>
            </ModalHeader>

            <Formik
              initialValues={{
                fromCaseId: "",
                toCaseId: "",
                expectCaseIds: "",
                comment: "",
                visibilityType: "all",
                isFromZoom: "",
                isLocalTranscriptionServer: 1,
                caseStatus: "",
                addressLabel: "",
                individualMail: false,
                contractNo: "",
                excel: "",
                typeOfLoad: "recordingProceeding",
                labelType: "withExcel",
                requestedMediators: [],
                fileName: "",
                resolutionKind: "arbitration",
                contestedFlag: true,
                whatsappRecipients: {
                  claimant: false,
                  respondent: false,
                  arbitrator: false,
                },
              }}
              enableReinitialize
              validationSchema={
                mode === inputModes.EXCEL
                  ? bulkUpdateValidationSchema?.shape({
                      fromCaseId: null,
                      toCaseId: null,
                    })
                  : bulkUpdateValidationSchema
              }
              validateOnBlur
              validateOnChange
              onSubmit={onFormSubmit}
            >
              {({
                values,
                handleChange,
                errors,
                handleSubmit,
                touched,
                setFieldValue,
                setFieldError,
                handleBlur,
                resetForm,
              }) => (
                <>
                  <ModalBody>
                    <FormSection>
                      <SectionLabel>Update Type</SectionLabel>
                      <TypeRadioGroup>
                        <RadioGroup
                          style={{ flexDirection: "row", gap: 4 }}
                          name="typeOfLoad"
                          value={values.typeOfLoad}
                          onChange={handleChange}
                        >
                          {radio_items.map((radio, key) => (
                            <FormControlLabel
                              key={key}
                              classes={{ label: classes.formLabel }}
                              value={radio.value}
                              disabled={radio.disabled}
                              control={
                                <Radio
                                  size="small"
                                  style={{ color: "#00838c" }}
                                />
                              }
                              label={radio.label}
                            />
                          ))}
                        </RadioGroup>
                      </TypeRadioGroup>
                    </FormSection>

                    {/* Contested Case Section */}
                    {values.typeOfLoad === "contestedCase" && (
                      <>
                        <FormSection>
                          <SectionLabel>Input Method</SectionLabel>
                          <ModeToggle>
                            <ModeBtn
                              type="button"
                              active={mode === inputModes.MANUAL}
                              onClick={() => {
                                setMode(inputModes.MANUAL);
                                setContestedExcelData([]);
                                setFieldValue("fromCaseId", "");
                                setFieldValue("toCaseId", "");
                                setFieldError("fromCaseId", "");
                                setFieldError("toCaseId", "");
                              }}
                            >
                              Enter ID Range
                            </ModeBtn>
                            <ModeBtn
                              type="button"
                              active={mode === inputModes.EXCEL}
                              onClick={() => {
                                setMode(inputModes.EXCEL);
                                setContestedExcelData([]);
                                setFieldValue("fromCaseId", "");
                                setFieldValue("toCaseId", "");
                                setFieldError("fromCaseId", "");
                                setFieldError("toCaseId", "");
                              }}
                            >
                              Upload Excel
                            </ModeBtn>
                          </ModeToggle>
                        </FormSection>

                        {mode === inputModes.MANUAL && (
                          <FormSection>
                            <InfoBanner>
                              Enter From and To Case ID range
                            </InfoBanner>
                            <InputGrid>
                              {inputs.slice(0, 3).map((input, key) => (
                                <FormField
                                  key={key}
                                  white
                                  menuItems={input.menuItems}
                                  {...{
                                    input,
                                    values,
                                    errors,
                                    touched,
                                    handleBlur,
                                    handleChange,
                                    setFieldValue,
                                  }}
                                />
                              ))}
                            </InputGrid>
                          </FormSection>
                        )}

                        {mode === inputModes.EXCEL && (
                          <FormSection>
                            <ExcelZone>
                              <label
                                htmlFor="contested-excel-upload"
                                style={{ cursor: "pointer", display: "block" }}
                              >
                                <HiddenInput
                                  id="contested-excel-upload"
                                  type="file"
                                  accept=".xlsx, .xls"
                                  onChange={(e) =>
                                    handleFileUpload(e, setFieldValue, true)
                                  }
                                />
                                <UploadInner
                                  loaded={contestedExcelData.length > 0}
                                >
                                  {contestedExcelData.length > 0 ? (
                                    <>
                                      <UploadCheck>
                                        <svg
                                          width="24"
                                          height="24"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2.5"
                                        >
                                          <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                      </UploadCheck>
                                      <UploadPrimary>
                                        {contestedExcelData.length}{" "}
                                        {contestedExcelType === "caseId"
                                          ? "Case IDs"
                                          : "Contract Numbers"}{" "}
                                        Loaded
                                      </UploadPrimary>
                                      <UploadSub>
                                        Click to replace file
                                      </UploadSub>
                                    </>
                                  ) : (
                                    <>
                                      <UploadCloud>
                                        <svg
                                          width="28"
                                          height="28"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="1.5"
                                        >
                                          <polyline points="16 16 12 12 8 16" />
                                          <line
                                            x1="12"
                                            y1="12"
                                            x2="12"
                                            y2="21"
                                          />
                                          <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                                        </svg>
                                      </UploadCloud>
                                      <UploadPrimary>
                                        Drop Excel file or click to browse
                                      </UploadPrimary>
                                      <UploadSub>
                                        Column: caseId or Contract Number
                                      </UploadSub>
                                    </>
                                  )}
                                </UploadInner>
                              </label>
                            </ExcelZone>
                          </FormSection>
                        )}

                        <FormSection>
                          <CheckboxContainer className="status_row">
                            <CustomCheckbox
                              checked={!!values.contestedFlag}
                              onChange={(e) =>
                                setFieldValue("contestedFlag", e.target.checked)
                              }
                            />
                            <CheckBoxLabel className="status_name">
                              Contested Flag
                            </CheckBoxLabel>
                          </CheckboxContainer>
                        </FormSection>
                      </>
                    )}

                    {/* Recording Proceeding Section */}
                    {values.typeOfLoad === "recordingProceeding" && (
                      <>
                        <FormSection>
                          <SectionLabel>Input Method</SectionLabel>
                          <ModeToggle>
                            <ModeBtn
                              type="button"
                              active={mode === inputModes.MANUAL}
                              onClick={() => {
                                setMode(inputModes.MANUAL);
                                setExcelCaseIds([]);
                                setFieldValue("fromCaseId", "");
                                setFieldValue("toCaseId", "");
                                setFieldError("fromCaseId", "");
                                setFieldError("toCaseId", "");
                              }}
                            >
                              Enter ID Range
                            </ModeBtn>
                            <ModeBtn
                              type="button"
                              active={mode === inputModes.EXCEL}
                              onClick={() => {
                                setMode(inputModes.EXCEL);
                                setExcelCaseIds([]);
                                setFieldValue("fromCaseId", "");
                                setFieldValue("toCaseId", "");
                                setFieldError("fromCaseId", "");
                                setFieldError("toCaseId", "");
                              }}
                            >
                              Upload Excel
                            </ModeBtn>
                          </ModeToggle>
                        </FormSection>

                        {mode === inputModes.MANUAL && (
                          <FormSection>
                            <InfoBanner>
                              Enter From and To Case ID range to update all
                              cases in that range
                            </InfoBanner>
                            <InputGrid>
                              {inputs.map((input, key) => (
                                <FormField
                                  key={key}
                                  white
                                  menuItems={input.menuItems}
                                  {...{
                                    input,
                                    values,
                                    errors,
                                    touched,
                                    handleBlur,
                                    handleChange,
                                    setFieldValue,
                                  }}
                                />
                              ))}
                            </InputGrid>
                          </FormSection>
                        )}

                        {mode === inputModes.EXCEL && (
                          <FormSection>
                            <ExcelZone>
                              <label
                                htmlFor="excel-upload-proceedings"
                                style={{ cursor: "pointer", display: "block" }}
                              >
                                <HiddenInput
                                  id="excel-upload-proceedings"
                                  type="file"
                                  accept=".xlsx, .xls"
                                  onChange={(e) =>
                                    handleFileUpload(e, setFieldValue)
                                  }
                                />
                                <UploadInner loaded={excelCaseIds.length > 0}>
                                  {excelCaseIds.length > 0 ? (
                                    <>
                                      <UploadCheck>
                                        <svg
                                          width="24"
                                          height="24"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2.5"
                                        >
                                          <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                      </UploadCheck>
                                      <UploadPrimary>
                                        {excelCaseIds.length} Case IDs Loaded
                                      </UploadPrimary>
                                      <UploadSub>
                                        Click to replace file
                                      </UploadSub>
                                    </>
                                  ) : (
                                    <>
                                      <UploadCloud>
                                        <svg
                                          width="28"
                                          height="28"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="1.5"
                                        >
                                          <polyline points="16 16 12 12 8 16" />
                                          <line
                                            x1="12"
                                            y1="12"
                                            x2="12"
                                            y2="21"
                                          />
                                          <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                                        </svg>
                                      </UploadCloud>
                                      <UploadPrimary>
                                        Drop Excel file or click to browse
                                      </UploadPrimary>
                                      <UploadSub>
                                        Column must be named <code>caseId</code>
                                      </UploadSub>
                                    </>
                                  )}
                                </UploadInner>
                              </label>
                            </ExcelZone>
                            {[
                              {
                                type: "select",
                                name: "resolutionKind",
                                label: "Case Type",
                                required: true,
                                menuItems: resolutionKindOptions,
                              },
                            ].map((input, key) => (
                              <FormField
                                key={key}
                                white
                                menuItems={input.menuItems}
                                {...{
                                  input,
                                  values,
                                  errors,
                                  touched,
                                  handleBlur,
                                  handleChange,
                                  setFieldValue,
                                }}
                              />
                            ))}
                          </FormSection>
                        )}
                      </>
                    )}

                    {/* Address Label Sub-Toggle */}
                    {values.typeOfLoad === "addressLabel" && (
                      <FormSection>
                        <SectionLabel>Label Type</SectionLabel>
                        <TypeRadioGroup>
                          <RadioGroup
                            style={{ flexDirection: "row", marginLeft: "10px" }}
                            name="labelType"
                            value={values.labelType}
                            onChange={(e) =>
                              setFieldValue("labelType", e.target.value)
                            }
                          >
                            {[
                              { label: "With Case Id's", value: "withCaseIds" },
                              { label: "With Excel", value: "withExcel" },
                            ].map((radio, key) => (
                              <FormControlLabel
                                key={key}
                                classes={{ label: classes.formLabel }}
                                value={radio.value}
                                control={
                                  <Radio
                                    size="small"
                                    style={{ color: "#7FA1C3" }}
                                  />
                                }
                                label={radio.label}
                              />
                            ))}
                          </RadioGroup>
                        </TypeRadioGroup>
                      </FormSection>
                    )}

                    {/* Non-recording, non-withExcel: show manual case ID range */}
                    {values?.typeOfLoad !== "addressLabel" &&
                      values?.labelType !== "withExcel" &&
                      values.typeOfLoad !== "recordingProceeding" &&
                      values.typeOfLoad !== "contestedCase" && (
                        <FormSection>
                          <InputGrid>
                            {inputs.map((input, key) => (
                              <FormField
                                key={key}
                                white
                                {...{
                                  input,
                                  values,
                                  errors,
                                  touched,
                                  handleBlur,
                                  handleChange,
                                  setFieldValue,
                                }}
                              />
                            ))}
                          </InputGrid>
                        </FormSection>
                      )}

                    {/* Case Status Search */}
                    {values?.typeOfLoad === "caseStatus" && (
                      <FormSection>
                        <SearchBar
                          placeHolderText="Search Sub Stages"
                          backgroundColor="#f9f9f9"
                          {...{ inputText, setInputText }}
                          width="100%"
                        />
                      </FormSection>
                    )}

                    {/* Assign Neutral Search */}
                    {values.typeOfLoad === "assignNeutral" && (
                      <FormSection>
                        <SearchBar
                          placeHolderText="Search Neutral"
                          backgroundColor="#f9f9f9"
                          {...{ inputText, setInputText }}
                          width="100%"
                        />
                      </FormSection>
                    )}

                    {/* Conditional content by typeOfLoad */}
                    {values.typeOfLoad === "caseStatus" ? (
                      <FormSection>
                        <StatusList>
                          {caseLifeCycleStatus?.length ? (
                            caseLifeCycleStatus
                              .filter((stage) =>
                                stage.label
                                  .toLowerCase()
                                  .includes(inputText.toLowerCase()),
                              )
                              .map((action) => (
                                <CheckboxContainer
                                  className="status_row"
                                  key={action.value}
                                >
                                  <CustomCheckBox
                                    name="caseStatus"
                                    checked={values.caseStatus === action.value}
                                    onChange={() =>
                                      setFieldValue(
                                        "caseStatus",
                                        values.caseStatus === action.value
                                          ? ""
                                          : action.value,
                                      )
                                    }
                                  />
                                  <CheckBoxLabel className="status_name">
                                    {action?.label}
                                  </CheckBoxLabel>
                                </CheckboxContainer>
                              ))
                          ) : (
                            <LoaderWrap>
                              <CircularProgress />
                            </LoaderWrap>
                          )}
                        </StatusList>
                      </FormSection>
                    ) : values.typeOfLoad === "assignNeutral" ? (
                      <FormSection>
                        <StatusList>
                          {loading ? (
                            <LoaderWrap>
                              <CircularProgress />
                            </LoaderWrap>
                          ) : neutralList?.length > 0 ? (
                            neutralList
                              .filter((arr) =>
                                arr.name
                                  .toLowerCase()
                                  .includes(inputText.toLowerCase()),
                              )
                              .map((neutral) => (
                                <CheckboxContainer
                                  className="status_row"
                                  key={neutral.id}
                                >
                                  <CustomCheckBox
                                    name="caseStatus"
                                    checked={values.requestedMediators.some(
                                      (m) => m.id === neutral?.id,
                                    )}
                                    onChange={() => {
                                      const isAlreadyAdded =
                                        values.requestedMediators.some(
                                          (m) => m.id === neutral?.id,
                                        );
                                      setFieldValue(
                                        "requestedMediators",
                                        isAlreadyAdded
                                          ? []
                                          : [
                                              {
                                                email: neutral?.email,
                                                name: neutral?.name,
                                                mobile: neutral?.mobile,
                                                id: neutral?.id,
                                              },
                                            ],
                                      );
                                    }}
                                  />
                                  <div
                                    className={`neutral_row ${neutral?.disabled ? "disabled" : ""}`}
                                  >
                                    <div className="neutral_name">
                                      <div>{neutral?.name}</div>
                                      <div>({neutral?.email})</div>
                                    </div>
                                  </div>
                                </CheckboxContainer>
                              ))
                          ) : (
                            <NoResult>No Neutrals found.</NoResult>
                          )}
                        </StatusList>
                        <CheckboxContainer
                          style={{ marginTop: "10px" }}
                          className="cm_row"
                        >
                          <CustomCheckbox
                            name="individualMail"
                            checked={values.individualMail}
                            onChange={handleChange}
                          />
                          <CheckBoxLabel>
                            Select if mail should go individually
                          </CheckBoxLabel>
                        </CheckboxContainer>
                      </FormSection>
                    ) : values?.typeOfLoad === "addressLabel" ? (
                      values.labelType === "withExcel" ? (
                        <FormSection>
                          {[
                            {
                              name: "contractNo",
                              label: "Upload Xlsx",
                              type: "upload",
                              required: true,
                              fileName: "excel",
                              accept: ".xlsx",
                              types: [
                                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                                "application/vnd.ms-excel",
                                "text/csv",
                              ],
                            },
                          ].map((upload, index) => (
                            <div style={{ marginTop: 10 }} key={index}>
                              <FieldLabel>
                                {upload.label} <RequiredStar>*</RequiredStar>
                              </FieldLabel>
                              <CustomFileUpload
                                error={
                                  upload.accept === ".xlsx"
                                    ? touched[upload.fileName] &&
                                      errors[upload.fileName]
                                    : touched[upload.name] &&
                                      errors[upload.name]
                                }
                                setFile={(file) =>
                                  s3Upload(
                                    file,
                                    upload.name,
                                    setFieldValue,
                                    upload.types,
                                    upload.accept,
                                    values,
                                    upload.fileName,
                                  )
                                }
                                style={{ width: "90%" }}
                                accept={upload.accept}
                                file={{
                                  name:
                                    upload.fileName === "excel"
                                      ? values[upload.fileName]
                                      : "",
                                }}
                              />
                            </div>
                          ))}
                        </FormSection>
                      ) : null
                    ) : values.typeOfLoad !== "contestedCase" ? (
                      <>
                        <FormSection>
                          <RadioGroup
                            style={{ flexDirection: "row", marginLeft: "10px" }}
                            name="isLocalTranscriptionServer"
                            value={values.isLocalTranscriptionServer}
                            onChange={(e) =>
                              setFieldValue(
                                "isLocalTranscriptionServer",
                                parseInt(e.target.value),
                              )
                            }
                          >
                            {server_items.map((radio, key) => (
                              <FormControlLabel
                                key={key}
                                classes={{ label: classes.formLabel }}
                                value={radio.value}
                                control={
                                  <Radio
                                    size="small"
                                    style={{ color: "#7E99A3" }}
                                  />
                                }
                                label={radio.label}
                              />
                            ))}
                          </RadioGroup>
                        </FormSection>
                        <FormSection>
                          <EditorRow>
                            <EditorContainer>
                              <ReactQuill
                                ref={quillRef}
                                value={comment}
                                onChange={(text) => setComment(text)}
                                modules={modules}
                                placeholder="Record the Proceedings…"
                              />
                            </EditorContainer>
                            <ButtonContainer>
                              <div className="vcLink">
                                <PrimaryCTAButton
                                  style={{ padding: "12px 22px" }}
                                  onClick={() => setModal({ state: true })}
                                >
                                  Add VC Link
                                </PrimaryCTAButton>
                              </div>
                              <PrimaryCTAButton
                                className="vcLink"
                                style={{ padding: "12px 22px" }}
                                aria-controls="comment-send-menu"
                                aria-haspopup="true"
                                onClick={handleMenuClick}
                                disabled={
                                  !comment
                                    ?.replace(ESCAPE_HTML_REGEX, "")
                                    .trim()
                                }
                              >
                                Send
                              </PrimaryCTAButton>
                              <div className="dictation">
                                <OrderDictation
                                  {...{ setComment }}
                                  isLocalTranscriptionServer={
                                    values.isLocalTranscriptionServer
                                  }
                                  width="30%"
                                />
                              </div>
                              <Menu
                                id="comment-send-menu"
                                anchorEl={anchorEl}
                                keepMounted
                                open={Boolean(anchorEl)}
                                onClose={handleMenuClose}
                                classes={{ paper: classes.paper }}
                              >
                                <Label>SEND TO</Label>
                                {[
                                  { label: "All", value: "all" },
                                  { label: "Claimant", value: "claimant" },
                                  { label: "Respondent", value: "respondent" },
                                  { label: "Neutral", value: "mediator" },
                                ].map((item) => (
                                  <MenuItem
                                    className={classes.menuRoot}
                                    key={item.value}
                                    onClick={() => {
                                      setFieldValue(
                                        "visibilityType",
                                        item.value,
                                      );
                                      setAnchorEl(null);
                                    }}
                                  >
                                    {item.label}
                                  </MenuItem>
                                ))}
                              </Menu>
                            </ButtonContainer>
                          </EditorRow>
                          <WhatsAppRow>
                            <SectionLabel style={{ margin: 0 }}>
                              <WhatsApp
                                style={{
                                  fill: COLORS.BTN_GREEN,
                                  fontSize: 18,
                                  verticalAlign: "middle",
                                  marginRight: 6,
                                }}
                              />
                              WhatsApp Notification
                            </SectionLabel>
                            {[
                              { label: "Claimant", value: "claimant" },
                              { label: "Respondent", value: "respondent" },
                              { label: "Arbitrator", value: "arbitrator" },
                            ].map((item) => (
                              <CheckboxContainer key={item.value}>
                                <CustomCheckBox
                                  id={`whatsapp-${item.value}`}
                                  checked={
                                    !!values.whatsappRecipients?.[item.value]
                                  }
                                  onChange={(e) =>
                                    setFieldValue(
                                      `whatsappRecipients.${item.value}`,
                                      e?.target?.checked,
                                    )
                                  }
                                />
                                <CheckBoxLabel
                                  htmlFor={`whatsapp-${item.value}`}
                                  style={{ fontSize: 14 }}
                                >
                                  {item.label}
                                </CheckBoxLabel>
                              </CheckboxContainer>
                            ))}
                          </WhatsAppRow>
                          <AddZoomLinkModal
                            {...{ modal, setModal }}
                            onSubmit={(data) => {
                              setFieldValue("isFromZoom", data);
                              setModal({ state: false });
                            }}
                          />
                        </FormSection>
                      </>
                    ) : null}
                  </ModalBody>

                  <ModalFooter>
                    <PrimaryOutlinedCTAButton
                      style={{ padding: "10px 18px" }}
                      onClick={() => {
                        resetForm();
                        setComment("");
                        setMode(inputModes.MANUAL);
                        setExcelCaseIds([]);
                        setContestedExcelData([]);
                      }}
                    >
                      Clear form
                    </PrimaryOutlinedCTAButton>
                    <PrimaryCTAButton
                      style={{ padding: "10px 24px" }}
                      onClick={handleSubmit}
                      disabled={
                        (values.typeOfLoad === "recordingProceeding" &&
                          mode === inputModes.EXCEL &&
                          excelCaseIds.length === 0) ||
                        (values.typeOfLoad === "contestedCase" &&
                          mode === inputModes.EXCEL &&
                          contestedExcelData.length === 0)
                      }
                    >
                      Submit
                    </PrimaryCTAButton>
                  </ModalFooter>
                </>
              )}
            </Formik>
          </ModalCard>
        </Overlay>
      </Modal>

      <ResponseModal
        open={showResponseModal?.state && !!showResponseModal?.metaData}
        onClose={() => {
          setShowResponseModal({ state: false });
          setBulkUpdate(false);
        }}
        responseData={showResponseModal?.metaData}
      />
    </>
  );
};

export default BulkUpdate;

const fadeIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -46%); }
  to   { opacity: 1; transform: translate(-50%, -50%); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
`;

const ModalCard = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 24px);
  max-width: 860px;
  background: #fff;
  border-radius: 20px;
  box-shadow:
    0 24px 80px rgba(0, 0, 0, 0.16),
    0 4px 16px rgba(0, 0, 0, 0.07);
  outline: none;
  animation: ${fadeIn} 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  display: flex;
  flex-direction: column;
  max-height: 95vh;
  & .loader {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }
  & .status_row {
    display: flex;
    flex-direction: row;
    padding: 10px 0;
    align-items: center;
    & .status_name {
      font-size: 15px;
      font-weight: 600;
      flex: 1;
      color: ${COLORS.COLOR_DARK};
      font-family: ${theme.fonts.primaryFontSemiBold};
    }
  }
  & .neutral_row {
    display: flex;
    flex-direction: row;
    padding: 10px 0;
    justify-content: center;
    align-items: center;
    &.disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
    & .neutral_name {
      font-size: 15px;
      font-weight: 600;
      flex: 1;
      color: ${COLORS.COLOR_DARK};
      font-family: ${theme.fonts.primaryFontSemiBold};
    }
  }
  @media (max-width: 480px) {
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    transform: none;
    width: 100%;
    border-radius: 20px 20px 0 0;
    max-height: 92vh;
    animation: none;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f2f5;
  position: relative;
  flex-shrink: 0;
`;

const HeaderAccent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  border-radius: 20px 20px 0 0;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
`;

const IconBadge = styled.div`
  width: 36px;
  height: 36px;
  background: ${COLORS.BTN_GREEN}18;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.BTN_GREEN};
  flex-shrink: 0;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontExtraBold};
  letter-spacing: -0.3px;
`;

export const CloseBtn = styled.button`
  width: 34px;
  height: 34px;
  border: none;
  background: #f4f6f8;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: all 0.18s;
  flex-shrink: 0;
  &:hover {
    background: #fee2e2;
    color: #ef4444;
    transform: rotate(90deg);
  }
`;

const ModalBody = styled.div`
  overflow-y: auto;
  padding: 20px 24px;
  flex: 1;
  scrollbar-width: thin;
  scrollbar-color: #e5e7eb transparent;
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e5e7eb;
    border-radius: 99px;
  }
  @media (max-width: 480px) {
    padding: 16px;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #f0f2f5;
  flex-shrink: 0;
`;

const FormSection = styled.div`
  margin-bottom: 18px;
`;

export const SectionLabel = styled.h4`
  margin: 0 0 10px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #9ca3af;
`;

const RequiredStar = styled.span`
  color: #ef4444;
  margin-left: 2px;
`;

export const TypeRadioGroup = styled.div`
  background: #f9fafb;
  border-radius: 10px;
  padding: 8px 12px;
  border: 1.5px solid #e5e7eb;
`;

const ModeToggle = styled.div`
  display: flex;
  background: #f4f6f8;
  border-radius: 12px;
  padding: 4px;
  gap: 4px;
`;

const ModeBtn = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  border: none;
  border-radius: 9px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  white-space: nowrap;
  ${(p) =>
    p.active
      ? css`
          background: #fff;
          color: ${COLORS.BTN_GREEN};
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          font-weight: 700;
        `
      : css`
          background: transparent;
          color: #6b7280;
          &:hover {
            background: rgba(255, 255, 255, 0.5);
          }
        `}
  @media (max-width: 360px) {
    font-size: 12px;
    padding: 9px 6px;
  }
`;

const InfoBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 10px 14px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12.5px;
  color: #1d4ed8;
  margin-bottom: 14px;
`;

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const ExcelZone = styled.div`
  border-radius: 14px;
  overflow: hidden;
  transition: border-color 0.2s;
  &:hover {
    border-color: ${COLORS.BTN_GREEN};
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const UploadInner = styled.div`
  padding: 28px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  background: ${(p) => (p.loaded ? `${COLORS.BTN_GREEN}08` : "#fafafa")};
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: ${COLORS.BTN_GREEN}08;
  }
`;

const UploadCloud = styled.div`
  color: #9ca3af;
  margin-bottom: 2px;
`;

const UploadCheck = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${COLORS.BTN_GREEN}20;
  color: ${COLORS.BTN_GREEN};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2px;
`;

const UploadPrimary = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const UploadSub = styled.div`
  font-size: 12px;
  color: #9ca3af;
  code {
    background: #f3f4f6;
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 11px;
    color: ${COLORS.BTN_GREEN};
  }
`;

const FieldLabel = styled.label`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: ${COLORS.COLOR_DARK};
  display: block;
  margin-bottom: 8px;
`;

const StatusList = styled.div`
  overflow-y: auto;
  max-height: 265px;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  padding: 6px 8px;
  scrollbar-width: thin;
  scrollbar-color: #e5e7eb transparent;
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e5e7eb;
    border-radius: 99px;
  }
`;

const LoaderWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 30px;
`;
const NoResult = styled.div`
  text-align: center;
  padding: 24px;
  color: #9ca3af;
  font-size: 14px;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const EditorRow = styled.div`
  display: flex;
`;

const WhatsAppRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 20px;
  margin-top: 14px;
`;

export const ToggleContainer = styled.label`
  position: relative;
  display: flex;
  width: 60px;
  height: 34px;
`;

export const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  &:checked + span {
    background-color: #4caf50;
  }
  &:checked + span:before {
    transform: translateX(26px);
  }
`;

export const Slider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 34px;
  &:before {
    position: absolute;
    content: "";
    height: 26px;
    width: 26px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    transition: 0.4s;
    border-radius: 50%;
  }
`;
